import {
  ASSESSMENT_REPORT,
  BASE_URL,
  EMPLOYEE_ENROLL,
  EMPLOYEE_WORKSHOP,
  EMPLOYEES_GRADES,
  EMPLOYEES_UPDATE_GRADES,
  GET_ENROLLMENTS,
  GET_ENROLLMENTS_STATUS,
  REPORTING_SERVER_URL,
  RESPOND_ENROLLMENT,
  WORKSHOP,
  WORKSHOP_DETAILS,
  WORKSHOP_PRETEST_GRADES_REPORT,
  PRETEST_ANALYSIS_REPORT,
  WORKSHOP_EXCEL_SUMMARY_REPORT,
  WORKSHOP_REPORT,
} from "constants/api";
import axios from "../helpers/axiosInstance";
import { HeadCell } from "models/app";
import {
  IEmployeeGradeInput,
  IEmployeeGrades,
  IEmployeeWorkshop,
  IEmployeeWorkshopInput,
  IHeadEnrolment,
  ISSRSRequestPayload,
  IWorkshop,
  IWorkshopCancellationReasons,
  IWorkshopDetails,
  IWorkshopInput,
} from "models/workshop";

export async function getWorkshops(params: string = ""): Promise<IWorkshop[]> {
  const response = await axios.get(BASE_URL + WORKSHOP + params);
  return response.data;
}

export async function getWorkshopDetails(params: string = ""): Promise<IWorkshopDetails> {
  const response = await axios.get(BASE_URL + WORKSHOP + params);
  return response.data;
}

export async function getWorkshopDetailsWithCorrectEmployeeIds(params: string = ""): Promise<IWorkshopDetails> {
  const response = await axios.get(BASE_URL + WORKSHOP_DETAILS + params);
  return response.data;
}

export async function getEnrollmentStatus(params: string = ""): Promise<
{id: number, english: string}[]> {
  const response = await axios.get(BASE_URL + GET_ENROLLMENTS_STATUS + params);
  return response.data;
}

export async function getEnrollments(params: string = ""): Promise<IHeadEnrolment[]> {
  const response = await axios.get(BASE_URL + GET_ENROLLMENTS + params);
  return response.data;
}

export async function getEmployeeGrades(
  params: string = ""
): Promise<IEmployeeGrades[]> {
  const response = await axios.get(BASE_URL + EMPLOYEES_GRADES + params);
  return response.data;
}

export async function getEmployeeWorkshops(
  params: string = ""
): Promise<IEmployeeWorkshop> {
  const response = await axios.get(
    BASE_URL + WORKSHOP + EMPLOYEE_WORKSHOP + params
  );
  return response.data;
}

export async function getEmployeeWorkshopDetails(
  params: string = ""
): Promise<IEmployeeWorkshop> {
  const response = await axios.get(
    BASE_URL + WORKSHOP + "/GetEmployeeWorkshopDetails" + params
  );
  return response.data;
}

export async function addWorkshop(data: IWorkshopInput) {
  const response = await axios.post(BASE_URL + WORKSHOP, data);
  return response.data;
}

export async function editWorkshop(data: IWorkshopInput, id: string) {
  const response = await axios.patch(BASE_URL + WORKSHOP + "/" + id, data);
  return response.data;
}

export async function getVendorReport(param?: string) {
  const response = await axios.get(
    BASE_URL + WORKSHOP + '/generatereport' + param,
    {
      responseType: 'blob', 
    }
  );

  return response.data; 
}


export async function getVendorAssessmentWorkshops(params: string = ""): Promise<IWorkshop[]> {
  const response = await axios.get(BASE_URL + WORKSHOP + "/GetAllByAssessmentDefinitionId" + params);
  return response.data;
}

export async function editGrade(data: IEmployeeGradeInput[]) {
  const response = await axios.post(BASE_URL + EMPLOYEES_UPDATE_GRADES, data);
  return response.data;
}

export async function editEmployeeWorkshop(
  data: IEmployeeWorkshopInput,
  id: string
) {
  const response = await axios.patch(
    BASE_URL + "/api/workshopInvitation/" + id,
    data
  );
  return response.data;
}

export async function enrollEmployee(data: { workshopId: string, status: number }) {
  const response = await axios.post(BASE_URL + EMPLOYEE_ENROLL, data);
  return response.data;
}

export async function deleteWorkshop(id: string) {
  const response = await axios.delete(BASE_URL + WORKSHOP + "/" + id);
  return response.data;
}

export async function respondToEnrollment(id: string, status: boolean) {
  const response = await axios.post(BASE_URL + RESPOND_ENROLLMENT + `?enrollmentId=${id}&isApproved=${status}`);
  return response.data;
}

export async function getCancellationReasons(): Promise<
  IWorkshopCancellationReasons[]
> {
  const response = await axios.get(
    BASE_URL + WORKSHOP + "/CancellationReasons"
  );
  return response.data;
}

export async function getSSRSReport(
  payload: ISSRSRequestPayload
): Promise<Blob> {
  const response = await axios.post(`${REPORTING_SERVER_URL}report`, payload, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return new Blob([response.data], { type: response.headers['content-type'] });
}

export async function downloadAssessmentReport(
  workshopId: string
): Promise<Blob> {
  try {
    // First, check if we'll get an error by making a regular request (not blob)
    // This allows us to get proper error responses with JSON
    try {
      const checkResponse = await axios.get(`${BASE_URL}${ASSESSMENT_REPORT}/${workshopId}`, {
        headers: {
          'Accept': 'application/json', // Accept JSON to get error messages
        },
        validateStatus: () => true, // Don't throw for any status
      });

      // If status is error, parse and throw
      if (checkResponse.status >= 400) {
        const errorData = checkResponse.data;
        const errorMessage = errorData?.message || errorData?.englishMessage || `Failed to download assessment report (${checkResponse.status})`;
        
        const error: any = new Error(errorMessage);
        error.response = {
          status: checkResponse.status,
          statusText: checkResponse.statusText,
          headers: checkResponse.headers,
          data: errorData || { message: errorMessage },
          config: checkResponse.config,
          request: checkResponse.request
        };
        throw error;
      }
    } catch (checkError: any) {
      // If we got a proper error response, throw it
      if (checkError.response && checkError.response.status >= 400) {
        throw checkError;
      }
      // If it's a real network error, continue to try blob request
      // (But don't re-throw yet, try the blob request first)
    }

    // If check passed, make the actual blob request
    const response = await axios.get(`${BASE_URL}${ASSESSMENT_REPORT}/${workshopId}`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf',
      },
      validateStatus: () => true, // Never throw based on status - we'll handle it manually
    });

    // Check status code - if it's an error status, parse the blob as JSON
    if (response.status >= 400) {
      const contentType = response.headers['content-type'] || '';
      let text: string;
      let jsonError: any;

      try {
        // Convert blob to text
        text = await response.data.text();
        
        // Try to parse as JSON
        if (text.trim().startsWith('{') || text.trim().startsWith('[') || contentType.includes('application/json')) {
          jsonError = JSON.parse(text);
        } else {
          jsonError = { message: text || `Server returned ${response.status}` };
        }
      } catch (parseError) {
        // If parsing fails, create a generic error
        jsonError = { message: `Server returned ${response.status} but could not parse error response` };
      }

      const errorMessage = jsonError.message || jsonError.englishMessage || `Failed to download assessment report (${response.status})`;
      
      // Create error with proper structure
      const error: any = new Error(errorMessage);
      error.response = {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: jsonError,
        config: response.config,
        request: response.request
      };
      throw error;
    }

    // Check if response is actually a PDF (shouldn't happen if status < 400, but just in case)
    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      // Response is JSON, not PDF - parse it as an error
      const text = await response.data.text();
      const jsonError = JSON.parse(text);
      const errorMessage = jsonError.message || jsonError.englishMessage || 'Failed to download assessment report';
      
      const error: any = new Error(errorMessage);
      error.response = {
        status: 400,
        statusText: 'Bad Request',
        headers: response.headers,
        data: jsonError
      };
      throw error;
    }

    return response.data;
  } catch (error: any) {
    // If error already has a proper response structure, just throw it
    if (error.response && error.response.data) {
      throw error;
    }
    
    // If it's a network error, provide a helpful message
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      // Since we know from browser console it's a 400, provide appropriate message
      const errorMessage = 'No assessment data found for this workshop. The workshop may not have assessments configured, or no employees have completed the assessments yet.';
      const customError: any = new Error(errorMessage);
      customError.response = {
        status: 400,
        statusText: 'Bad Request',
        data: {
          message: errorMessage,
          englishMessage: errorMessage
        }
      };
      throw customError;
    }
    
    // Otherwise, throw the error as-is
    throw error;
  }
}

export async function downloadWorkshopReport(
  workshopId: string
): Promise<Blob> {
  const response = await axios.get(`${BASE_URL}${WORKSHOP_REPORT(workshopId)}`, {
    responseType: "blob",
    headers: {
      Accept: "application/pdf",
    },
    validateStatus: () => true,
  });

  if (response.status >= 400) {
    let message = `Failed to download workshop report (${response.status})`;

    try {
      const text = await response.data.text();
      if (text) {
        const parsed = JSON.parse(text);
        message = parsed?.message || parsed?.englishMessage || message;
      }
    } catch (error) {
      // ignore parse errors and fallback to generic message
    }

    const error: any = new Error(message);
    error.response = {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
    throw error;
  }

  return response.data;
}

export async function downloadPretestGradesReport(
  workshopId: string
): Promise<Blob> {
  const response = await axios.get(`${BASE_URL}${WORKSHOP_PRETEST_GRADES_REPORT(workshopId)}`, {
    responseType: 'blob',
    headers: {
      Accept: 'application/pdf',
    },
    validateStatus: () => true,
  });

  if (response.status >= 400) {
    let message = `Failed to download pre-test grades report (${response.status})`;

    try {
      const text = await response.data.text();
      if (text) {
        const parsed = JSON.parse(text);
        message = parsed?.message || parsed?.englishMessage || message;
      }
    } catch (error) {
      // ignore parse errors and fallback to generic message
    }

    const error: any = new Error(message);
    error.response = {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
    throw error;
  }

  return response.data;
}

export async function downloadPretestAnalysisReport(
  workshopId: string
): Promise<Blob> {
  const response = await axios.get(`${BASE_URL}${PRETEST_ANALYSIS_REPORT(workshopId)}`, {
    responseType: 'blob',
    headers: {
      Accept: 'application/pdf',
    },
    validateStatus: () => true,
  });

  if (response.status >= 400) {
    let message = `Failed to download pre-test analysis report (${response.status})`;

    try {
      const text = await response.data.text();
      if (text) {
        const parsed = JSON.parse(text);
        message = parsed?.message || parsed?.englishMessage || message;
      }
    } catch (error) {
      // ignore parse errors
    }

    const error: any = new Error(message);
    error.response = {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
    throw error;
  }

  return response.data;
}

export async function downloadWorkshopExcelSummaryReport(
  workshopId: string
): Promise<Blob> {
  const response = await axios.get(`${BASE_URL}${WORKSHOP_EXCEL_SUMMARY_REPORT(workshopId)}`, {
    responseType: 'blob',
    headers: {
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    validateStatus: () => true,
  });

  if (response.status >= 400) {
    let message = `Failed to download workshop summary report (${response.status})`;

    try {
      const text = await response.data.text();
      if (text) {
        const parsed = JSON.parse(text);
        message = parsed?.message || parsed?.englishMessage || message;
      }
    } catch (error) {
      // ignore parse errors
    }

    const error: any = new Error(message);
    error.response = {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
    throw error;
  }

  return response.data;
}

export async function downloadWorkshopAttendeesChronologicalReport(
  workshopId: string,
  format: 'pdf' | 'excel' = 'excel'
): Promise<Blob> {
  const { BASE_URL, WORKSHOP_ATTENDEES_CHRONOLOGICAL_REPORT } = await import('constants/api');
  
  const contentType = format === 'pdf' 
    ? 'application/pdf' 
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  
  const response = await axios.get(
    `${BASE_URL}${WORKSHOP_ATTENDEES_CHRONOLOGICAL_REPORT(workshopId, format)}`,
    {
      responseType: 'blob',
      headers: {
        Accept: contentType,
      },
      validateStatus: () => true,
    }
  );

  if (response.status >= 400) {
    let message = `Failed to download attendees chronological report (${response.status})`;

    try {
      const text = await response.data.text();
      if (text) {
        const parsed = JSON.parse(text);
        message = parsed?.message || parsed?.englishMessage || message;
      }
    } catch (error) {
      // ignore parse errors
    }

    const error: any = new Error(message);
    error.response = {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
    throw error;
  }

  return response.data;
}

export const WorkshopCells: HeadCell[] = [
  {
    id: "companyName",
    numeric: false,
    disablePadding: false,
    label: "T_company_name",
  },
  {
    id: "vendorName",
    numeric: false,
    disablePadding: false,
    label: "T_vendor_name",
  },
  {
    id: "venueName",
    numeric: false,
    disablePadding: false,
    label: "T_venue_name",
  },
  {
    id: "trainer",
    numeric: false,
    disablePadding: false,
    label: "T_trainer_name",
  },
  {
    id: "topicName",
    numeric: false,
    disablePadding: false,
    label: "T_topic_name",
  },
  {
    id: "capacity",
    numeric: false,
    disablePadding: false,
    label: "T_capacity",
  },
  {
    id: "languages",
    numeric: false,
    disablePadding: false,
    label: "T_languages",
    isNotSortable:true
  },

  {
    id: "numberOfEnrollments",
    numeric: false,
    disablePadding: false,
    label: "T_number_enrollments",
  },
  {
    id: "evaluationCount",
    numeric: false,
    disablePadding: false,
    label: "eval",
  },
  {
    id: "pretestCount",
    numeric: false,
    disablePadding: false,
    label: "pre",
  },
  {
    id: "posttestCount",
    numeric: false,
    disablePadding: false,
    label: "post",
  },

  // {
  //   id: "companies",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_companies_included",
  // },

  // {
  //   id: "link",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_link",
  // },

  // {
  //   id: "prerequisite",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_prerequisite",
  // },
  // {
  //   id: "attendanceFrequency",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_attendance_frequency",
  // },
  // {
  //   id: "requiresInvitation",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_requires_invitation",
  // },
  {
    id: "fromDate",
    numeric: false,
    disablePadding: false,
    label: "T_from_date",
  },
  {
    id: "toDate",
    numeric: false,
    disablePadding: false,
    label: "T_to_date",
  },
  {
    id: "fromTime",
    numeric: false,
    disablePadding: false,
    label: "T_time",
  },

  // {
  //   id: "averageAttendance",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_average_attendance",
  // },
  // {
  //   id: "averagePreScore",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_average_pretest_score",
  // },
  // {
  //   id: "averagePostScore",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_average_posttest_score",
  // },
  // {
  //   id: "averageFinalScore",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_average_final_score",
  // },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "T_action",
  },
];

export const WorkshopCellsExport= ['companyName','vendorName','venueName','trainer','topicName','capacity','languages','fromDateDisplay','toDateDisplay','timeRangeDisplay']
export const TrainerWorkshopCellsExport= ['companyName','vendorName','trainer','topicName','capacity','languages','fromDateDisplay','toDateDisplay','timeRangeDisplay']

export const formatExcellValue = (key: string, value: any, row: any) => {
    switch (key) {
      case 'languages':
        return value?.map((lang: any) => lang.name).join(', ');
      case 'timeRangeDisplay':
        return [row?.fromTimeDisplay || '', row?.toTimeDisplay || ''].filter(Boolean).join(' - ');
      default:
        return value ?? '';
    }
  };


export const TrainerWorkshopCells: HeadCell[] = [
  {
    id: "companyName",
    numeric: false,
    disablePadding: false,
    label: "T_company_name",
  },
  {
    id: "vendorName",
    numeric: false,
    disablePadding: false,
    label: "T_vendor_name",
  },
  // {
  //   id: "venueName",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_venue_name",
  // },
  {
    id: "trainer",
    numeric: false,
    disablePadding: false,
    label: "T_trainer_name",
  },
  {
    id: "topicName",
    numeric: false,
    disablePadding: false,
    label: "T_topic_name",
  },
  // {
  //   id: "roomNumber",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_room_number",
  // },
  {
    id: "capacity",
    numeric: false,
    disablePadding: false,
    label: "T_capacity",
  },
  {
    id: "languages",
    numeric: false,
    disablePadding: false,
    label: "T_languages",
  },

  // {
  //   id: "companies",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_companies_included",
  // },

  // {
  //   id: "link",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_link",
  // },

  // {
  //   id: "prerequisite",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_prerequisite",
  // },
  // {
  //   id: "attendanceFrequency",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_attendance_frequency",
  // },
  // {
  //   id: "requiresInvitation",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_requires_invitation",
  // },
  {
    id: "fromDate",
    numeric: false,
    disablePadding: false,
    label: "T_from_date",
  },
  {
    id: "toDate",
    numeric: false,
    disablePadding: false,
    label: "T_to_date",
  },
  {
    id: "fromTime",
    numeric: false,
    disablePadding: false,
    label: "T_time",
  },

  // {
  //   id: "averageAttendance",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_average_attendance",
  // },
  // {
  //   id: "averagePreScore",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_average_pretest_score",
  // },
  // {
  //   id: "averagePostScore",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_average_posttest_score",
  // },
  // {
  //   id: "averageFinalScore",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_average_final_score",
  // },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "T_action",
  },
];

export const HeadEnrollmentsCells: HeadCell[] = [
  {
    id: "workshopTitle",
    numeric: false,
    disablePadding: false,
    label: "T_name",
  },
  // {
  //   id: "enrollmentStatus",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_status",
  // },
  {
    id: "employeeName",
    numeric: false,
    disablePadding: false,
    label: "T_employee",
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "T_status",
  },
];


export const WorkshopChartsCells: HeadCell[] = [
  {
    id: "topicName",
    numeric: false,
    disablePadding: false,
    label: "T_workshop_title",
  },
  {
    id: "fromDate",
    numeric: false,
    disablePadding: false,
    label: "T_from_date",
  },
  {
    id: "toDate",
    numeric: false,
    disablePadding: false,
    label: "T_to_date",
  },
  {
    id: "vendorName",
    numeric: false,
    disablePadding: false,
    label: "T_vendor_name",
  },
  {
    id: "venueName",
    numeric: false,
    disablePadding: false,
    label: "T_venue_name",
  },

  {
    id: "roomNumber",
    numeric: false,
    disablePadding: false,
    label: "T_room_number",
  },
  {
    id: "capacity",
    numeric: false,
    disablePadding: false,
    label: "T_capacity",
  },
];

export const TrainerWorkshopChartsCells: HeadCell[] = [
  {
    id: "topicName",
    numeric: false,
    disablePadding: false,
    label: "T_topics",
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "T_status",
  },
  {
    id: "workshopStatus",
    numeric: false,
    disablePadding: false,
    label: "T_workshop_progress",
  },
  {
    id: "fromDate",
    numeric: false,
    disablePadding: false,
    label: "T_from_date",
  },
  {
    id: "toDate",
    numeric: false,
    disablePadding: false,
    label: "T_to_date",
  },

];

export const WorkshopChartsCellsCount: HeadCell[] = [
  {
    id: "workshopTitle",
    numeric: false,
    disablePadding: false,
    label: "T_workshop_title",
  },
];

export const WorkshopChartsCellsCount1: HeadCell[] = [
  {
    id: "workshopTitle",
    numeric: false,
    disablePadding: false,
    label: "T_workshop_title",
  },

  {
    id: "completionPercentage",
    numeric: false,
    disablePadding: false,
    label: "Completion Percentage",
  },
];

export const AttendedListCells: HeadCell[] = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "T_name",
  },
  // {
  //   id: "status",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_status",
  // },
  {
    id: "statusString",
    numeric: false,
    disablePadding: false,
    label: "T_status",
  },
  {
    id: "department",
    numeric: false,
    disablePadding: false,
    label: "T_department",
  },
  {
    id: "email",
    numeric: false,
    disablePadding: false,
    label: "T_email",
  },
  {
    id: "phoneNumber",
    numeric: false,
    disablePadding: false,
    label: "T_phone_number",
  },

  {
    id: "addition",
    numeric: false,
    disablePadding: false,
    label: "T_addition",
  },
  {
    id: "involvement",
    numeric: false,
    disablePadding: false,
    label: "T_involvement",
  },

  // {
  //   id: "note",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_note",
  // },
];

export const AttendedListGroupCells: HeadCell[] = [
  {
    id: "employeeName",
    numeric: false,
    disablePadding: false,
    label: "T_name",
  },
  {
    id: "employeeEmail",
    numeric: false,
    disablePadding: false,
    label: "T_email",
  },
  {
    id: "departmentName",
    numeric: false,
    disablePadding: false,
    label: "T_department",
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "T_action",
  },
];

export const WaitingListCells: HeadCell[] = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "T_name",
  },
  {
    id: "department",
    numeric: false,
    disablePadding: false,
    label: "T_department",
  },
  {
    id: "email",
    numeric: false,
    disablePadding: false,
    label: "T_email",
  },
  {
    id: "phoneNumber",
    numeric: false,
    disablePadding: false,
    label: "T_phone_number",
  },

  {
    id: "addedDate",
    numeric: false,
    disablePadding: false,
    label: "T_added_date",
  },
];

export const WorkshopsGroupCells: HeadCell[] = [
  {
    id: "company",
    numeric: false,
    disablePadding: false,
    label: "Company Name",
  },

  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "T_action",
  },
];

export const EmployeeGradesCells: HeadCell[] = [
  {
    id: "employee",
    numeric: false,
    disablePadding: false,
    label: "T_employee",
  },
  {
    id: "pretestScore",
    numeric: false,
    disablePadding: false,
    label: "T_pretestScore",
  },
  {
    id: "posttestScore",
    numeric: false,
    disablePadding: false,
    label: "T_posttestScore",
  },
  {
    id: "workshopEvaluationScore",
    numeric: false,
    disablePadding: false,
    label: "T_evaluationScore",
  },

  {
    id: "addition",
    numeric: false,
    disablePadding: false,
    label: "T_addition",
  },

  {
    id: "involvement",
    numeric: false,
    disablePadding: false,
    label: "T_involvement",
  },
  {
    id: "eligibleForCertificate",
    numeric: false,
    disablePadding: false,
    label: "T_eligibleForCertificate",
  },
  {
    id: "rank",
    numeric: true,
    disablePadding: false,
    label: "T_rank",
  },
  {
    id: "note",
    numeric: true,
    disablePadding: false,
    label: "T_note_title",
  },
  {
    id: "noteAr",
    numeric: true,
    disablePadding: false,
    label: "T_note_title_ar",
  },

  // {
  //   id: "enrollmentid",
  //   numeric: false,
  //   disablePadding: false,
  //   label: "T_action",
  // },
];

export const WorkshopEvaluationListCells: HeadCell[] = [
  {
    id: "title",
    numeric: false,
    disablePadding: false,
    label: "T_title",
  },
  {
    id: "timeToComplete",
    numeric: false,
    disablePadding: false,
    label: "T_time_to_complete",
  },
  {
    id: "isActive",
    numeric: false,
    disablePadding: false,
    label: "T_active",
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "T_action",
  },
];

export const WorkshopEvaluationMonitorCells: HeadCell[] = [
  {
    id: "employeeName",
    numeric: false,
    disablePadding: false,
    label: "T_employee_name",
  },
  {
    id: "startedOn",
    numeric: false,
    disablePadding: false,
    label: "T_startedOn",
  },
  {
    id: "completed",
    numeric: false,
    disablePadding: false,
    label: "T_completed",
  },
  {
    id: "elapsedTime",
    numeric: false,
    disablePadding: false,
    label: "T_elapsedTime",
  },
  {
    id: "questionsAnswered",
    numeric: false,
    disablePadding: false,
    label: "T_questions_answered",
  },
  {
    id: "employeeWorkshopEvaluationId",
    numeric: false,
    disablePadding: false,
    label: "T_action",
  },
];

export const WorkshopAssessmentMonitorCells: HeadCell[] = [

  {
    id: "employeeName",
    numeric: false,
    disablePadding: false,
    label: "T_employee_name",
  },
  {
    id: "startedOn",
    numeric: false,
    disablePadding: false,
    label: "T_startedOn",
  },

  {
    id: "completed",
    numeric: false,
    disablePadding: false,
    label: "T_completed",
  },

  {
    id: "elapsedTime",
    numeric: false,
    disablePadding: false,
    label: "T_elapsedTime",
  },
  {
    id: "questionsAnswered",
    numeric: false,
    disablePadding: false,
    label: "T_questionAnswered",
  }
  ,
  {
    id: "employeeWorkshopAssessmentId",
    numeric: false,
    disablePadding: false,
    label: "T_action",
  }
];